/**
 * DecisionFlow — RouteGuard v2
 * S73-UI-01
 *
 * Behavior:
 *   - Booting (initial /auth/me probe) → loading state
 *   - Not authenticated → /login (preserves attempted path in state)
 *   - Authenticated, on /login → redirect to shell root for user_type
 *   - Authenticated, on wrong shell route → redirect to own shell root (silent)
 *   - Authenticated, on correct shell or sub-route → render children
 *   - Unknown / missing user_type → /login
 *
 * Shell registry (DD-04, spec v1.3 §1.2):
 *   comp_admin        → /admin/
 *   finance           → /finance/
 *   manager           → /manager/
 *   rep               → /rep/
 *   tenant_super_admin → /tenant-admin/   (holding screen — no backend yet)
 *
 * Security note: server-side RBAC enforces access on every API call.
 * This component is UX routing only — not a security boundary.
 *
 * DD-03: BrowserRouter lives in main.jsx. RouteGuard wraps protected
 * routes inside App.jsx <Routes> only — never wraps the entire app tree.
 *
 * DD-05: Role always from JWT user_type. Never from UI state in production.
 */

import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext.jsx";

// Shell root map — single source of truth for role → route root.
// Must stay in sync with spec v1.3 §1.2 shell registry.
// AFTER
const SHELL_ROOT = {
  comp_admin:         "/admin/",
  finance:            "/finance/",
  manager:            "/manager/",
  rep:                "/rep/",
  tenant_super_admin: "/tenant-admin/",
  tool_admin:         "/platform-admin/",
  platform_admin:     "/platform-admin/",
};

function getShellRoot(userType) {
  return SHELL_ROOT[userType] ?? null;
}

const SHARED_ROUTES = ['/onboarding'];

function isOnOwnShell(pathname, userType) {
  // Shared routes accessible to multiple roles
  if (SHARED_ROUTES.some((r) => pathname === r || pathname.startsWith(r + '/'))) {
    // AFTER
    return ['comp_admin', 'tenant_super_admin', 'tool_admin', 'platform_admin'].includes(userType);
  }
  const root = getShellRoot(userType);
  if (!root) return false;
  // /login is public — not a shell route
  if (pathname === "/login") return false;
  return pathname === root || pathname.startsWith(root);
}

export default function RouteGuard({ children }) {
  const { user, booted } = useAuth();
  const location = useLocation();

  // --- Booting ---
  if (!booted) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        color: "var(--color-text-tertiary)",
        fontSize: "var(--font-size-sm)",
      }}>
        Loading…
      </div>
    );
  }

  // --- Not authenticated ---
  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  // --- Authenticated: resolve shell root ---
  const shellRoot = getShellRoot(user.user_type);

  // Unknown user_type — cannot route safely, send to login
  if (!shellRoot) {
    return <Navigate to="/login" replace />;
  }

  // --- Authenticated, on /login → redirect to shell root ---
  if (location.pathname === "/login") {
    return <Navigate to={shellRoot} replace />;
  }

  // --- Authenticated, wrong shell → redirect to own shell root (silent) ---
  // Per spec §2.3: no error message, silent redirect only.
  if (!isOnOwnShell(location.pathname, user.user_type)) {
    return <Navigate to={shellRoot} replace />;
  }

  // --- Correct shell → render ---
  return children;
}
